import { memo, useCallback } from 'react'

import { useFragment } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0ButtonDropdown, F0Icon } from '@factorialco/f0-react'
import { CheckDouble as CheckDoubleIcon } from '@factorialco/f0-react/icons/app'
import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'
import { InboxTaskListItemCompletionFragmentDoc } from 'generated/resources/graphql'
import { TasksValueObjectsTaskKindEnum } from 'generated/resources/graphql'

import { useActivity } from 'modules/core/lib/activities/activity'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getTaskActions } from 'modules/inbox_tasks/lib/actions'
import { trackClickInboxAction } from 'modules/inbox_tasks/lib/events'

type ActionCallback = (params: { taskId: number }) => Promise<boolean | void>

type DropdownActionCallback = (params: {
  taskId: number
  actionId: string
}) => Promise<boolean | void>

type Props = {
  taskId: number
  taskKind?: TasksValueObjectsTaskKindEnum
  status?: TasksValueObjectsTaskStatusEnum
  onClickPrimaryAction?: ActionCallback
  onClickSecondaryAction?: ActionCallback
  onClickDropdownAction?: DropdownActionCallback
  actionsDisabled?: boolean
  approvableTypename?: string
  approvalSuggestion?: string
}

function Footer({
  taskId,
  taskKind,
  status,
  onClickPrimaryAction,
  onClickSecondaryAction,
  onClickDropdownAction,
  actionsDisabled = false,
  approvableTypename,
  approvalSuggestion,
}: Props) {
  const { data: fragmentData } = useFragment({
    fragment: InboxTaskListItemCompletionFragmentDoc,
    fragmentName: 'InboxTaskListItemCompletion',
    from: {
      __typename: 'TasksInboxTask',
      id: taskId,
    },
  })

  const { closeActivity } = useActivity()

  const completedAtDate = fragmentData?.completedAt

  const { primaryAction, secondaryAction, dropdownActions } = getTaskActions({
    taskKind: taskKind ?? 'manual',
    status,
    approvableTypename,
  })

  const completedAt = completedAtDate
    ? i18nDateToString(stringToDate(completedAtDate, 'backend_datetime'), 'ui_short_with_time')
    : undefined

  const handlePrimaryAction = useCallback(async () => {
    if (!primaryAction) return

    trackClickInboxAction({
      type: primaryAction.id,
      approvable_typename: approvableTypename,
      approval_suggestion: approvalSuggestion,
    })

    const resolved = await onClickPrimaryAction?.({ taskId })
    if (resolved) {
      closeActivity()
    }
  }, [
    primaryAction,
    onClickPrimaryAction,
    taskId,
    closeActivity,
    approvableTypename,
    approvalSuggestion,
  ])

  const handleSecondaryAction = useCallback(async () => {
    if (!secondaryAction) return

    trackClickInboxAction({
      type: secondaryAction.id,
      approvable_typename: approvableTypename,
      approval_suggestion: approvalSuggestion,
    })

    const resolved = await onClickSecondaryAction?.({ taskId })
    if (resolved) {
      closeActivity()
    }
  }, [
    secondaryAction,
    onClickSecondaryAction,
    taskId,
    closeActivity,
    approvableTypename,
    approvalSuggestion,
  ])

  const handleDropdownAction = useCallback(
    async (actionId: string) => {
      trackClickInboxAction({
        type: actionId,
        approvable_typename: approvableTypename,
        approval_suggestion: approvalSuggestion,
      })

      const resolved = await onClickDropdownAction?.({ taskId, actionId })
      if (resolved) {
        closeActivity()
      }
    },
    [onClickDropdownAction, taskId, closeActivity, approvableTypename, approvalSuggestion]
  )

  if (!!completedAt && taskKind !== 'manual') {
    return (
      <Box flexDirection="row" justifyContent="flexEnd" gap="s4" flexGrow>
        <F0Icon icon={CheckDoubleIcon} color="secondary" />
        <Text color="grey700" size="200">
          {`${i18n.t('tasks.labels.completed_at')} ${completedAt}`}
        </Text>
      </Box>
    )
  }

  return (
    <Box flexDirection="row" gap="s12">
      {primaryAction && (
        <F0Button
          label={primaryAction.label}
          icon={primaryAction.icon}
          disabled={actionsDisabled}
          onClick={handlePrimaryAction}
        />
      )}
      {secondaryAction && (
        <F0Button
          label={secondaryAction.label}
          variant="critical"
          icon={secondaryAction.icon}
          disabled={actionsDisabled}
          onClick={handleSecondaryAction}
        />
      )}
      {dropdownActions && (
        <F0ButtonDropdown items={dropdownActions} onClick={handleDropdownAction} />
      )}
    </Box>
  )
}

export default memo(Footer)
